import React, { useState } from "react";
// Chakra imports
import {
    Flex,
    Button,
    FormControl,
    FormLabel,
    Heading,
    Input,
    Text,
    FormErrorMessage,
    useToast,
    Alert,
    AlertIcon,
    AlertTitle,
    AlertDescription,
    Box,
    VStack,
    Icon,
    InputGroup,
    InputLeftElement,
    Image,
    useColorModeValue,
} from "@chakra-ui/react";
import { useLoading } from "Providers/LoadingProvider";
import { Field, Form, Formik } from "formik";
import AuthApi from "Api/AuthApi";
import { Link } from "react-router-dom";
import { TbMail, TbKey } from "react-icons/tb";

function Recover() {
    const loading = useLoading();
    const [mailSent, setMailSent] = useState(false);
    const toast = useToast({
        position: "top-right",
    });

    let submitHandle = async (values, { setFieldError }) => {
        loading.show();
        let response = await AuthApi.recover(values);
        if (response.status !== 200) {
            if (response.status === 422) {
                setFieldError('email', 'E-mail não encontrado!');
            } else {
                toast({ status: 'error', title: 'Erro ao redefinir senha!', duration: 3000, variant: 'solid', isClosable: true });
            }
            loading.hide();
        } else {
            setMailSent(true);
            loading.hide();
        }
    }

    // Dark mode colors
    const cardBg = useColorModeValue("white", "gray.800");
    const inputBg = useColorModeValue("gray.50", "gray.700");
    const inputBorder = useColorModeValue("gray.200", "gray.600");
    const inputHoverBorder = useColorModeValue("gray.300", "gray.500");
    const inputFocusBg = useColorModeValue("white", "gray.700");
    const labelColor = useColorModeValue("gray.700", "gray.200");
    const textMuted = useColorModeValue("gray.500", "gray.400");
    const iconColor = useColorModeValue("gray.400", "gray.500");
    const linkColor = useColorModeValue("#2D6B3F", "green.300");

    return (
        <Flex w="100%" justify="center" px={4}>
            <Box
                w={{ base: "100%", md: "420px" }}
                bg={cardBg}
                borderRadius="2xl"
                boxShadow="0 25px 50px -12px rgba(0, 0, 0, 0.25)"
                overflow="hidden"
            >
                {/* Header */}
                <Box
                    bg="linear-gradient(135deg, #2D6B3F 0%, #1F5A2F 100%)"
                    py={8}
                    px={6}
                    textAlign="center"
                >
                    <Link to="/">
                        <Image
                            src="/wsclima-logo.png"
                            alt="WS Clima Logo"
                            h="60px"
                            w="auto"
                            mx="auto"
                            mb={3}
                            objectFit="contain"
                        />
                    </Link>
                    <Heading color="white" fontSize="2xl" fontWeight="bold">
                        Recuperar Senha
                    </Heading>
                    <Text color="whiteAlpha.800" fontSize="sm" mt={2}>
                        Digite seu e-mail para receber o link de recuperação
                    </Text>
                </Box>

                {/* Content */}
                <Box p={8}>
                    {mailSent ? (
                        <Alert
                            status='success'
                            variant='subtle'
                            flexDirection='column'
                            alignItems='center'
                            justifyContent='center'
                            textAlign='center'
                            borderRadius="xl"
                            py={6}
                        >
                            <AlertIcon boxSize='40px' mr={0} />
                            <AlertTitle mt={4} mb={1} fontSize='lg'>
                                E-mail enviado!
                            </AlertTitle>
                            <AlertDescription maxWidth='sm'>
                                Você receberá um e-mail em poucos instantes!<br/>
                                Clique no botão dentro dele para alterar sua senha.
                            </AlertDescription>
                        </Alert>
                    ) : (
                        <Formik onSubmit={submitHandle} initialValues={{ email: "" }}>
                            <Form>
                                <VStack spacing={5}>
                                    <Field name="email">
                                        {({ field, form }) => (
                                            <FormControl isInvalid={form.errors.email && form.touched.email}>
                                                <FormLabel fontSize="sm" fontWeight="medium" color={labelColor}>
                                                    E-mail
                                                </FormLabel>
                                                <InputGroup size="lg">
                                                    <InputLeftElement pointerEvents="none" h="100%">
                                                        <Icon as={TbMail} color={iconColor} />
                                                    </InputLeftElement>
                                                    <Input
                                                        {...field}
                                                        borderRadius="xl"
                                                        fontSize="sm"
                                                        type="email"
                                                        placeholder="seu@email.com"
                                                        bg={inputBg}
                                                        border="2px solid"
                                                        borderColor={inputBorder}
                                                        _hover={{ borderColor: inputHoverBorder }}
                                                        _focus={{ borderColor: "#2D6B3F", bg: inputFocusBg, boxShadow: "0 0 0 1px #2D6B3F" }}
                                                    />
                                                </InputGroup>
                                                <FormErrorMessage>{form.errors.email}</FormErrorMessage>
                                            </FormControl>
                                        )}
                                    </Field>

                                    <Button
                                        type="submit"
                                        bg="linear-gradient(135deg, #2D6B3F 0%, #1F5A2F 100%)"
                                        color="white"
                                        w="100%"
                                        h="50px"
                                        borderRadius="xl"
                                        fontSize="md"
                                        fontWeight="semibold"
                                        _hover={{
                                            bg: "linear-gradient(135deg, #1F5A2F 0%, #164A24 100%)",
                                            transform: "translateY(-2px)",
                                            boxShadow: "lg",
                                        }}
                                        _active={{
                                            transform: "translateY(0)",
                                        }}
                                        transition="all 0.2s"
                                        leftIcon={<TbKey />}
                                    >
                                        Enviar link de recuperação
                                    </Button>
                                </VStack>
                            </Form>
                        </Formik>
                    )}

                    {/* Link */}
                    <VStack spacing={3} mt={6}>
                        <Text color={textMuted} fontSize="sm" textAlign="center">
                            Lembrou sua senha?{" "}
                            <Link to="/auth/signin">
                                <Text as="span" color={linkColor} fontWeight="bold" _hover={{ textDecoration: "underline" }}>
                                    Faça login
                                </Text>
                            </Link>
                        </Text>
                    </VStack>
                </Box>
            </Box>
        </Flex>
    );
}

export default Recover;
